/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialRegulation } from './SpecialRegulation';
import {
    SpecialRegulationFromJSON,
    SpecialRegulationFromJSONTyped,
    SpecialRegulationToJSON,
    SpecialRegulationToJSONTyped,
} from './SpecialRegulation';
import type { Value } from './Value';
import {
    ValueFromJSON,
    ValueFromJSONTyped,
    ValueToJSON,
    ValueToJSONTyped,
} from './Value';
import type { Dimensions } from './Dimensions';
import {
    DimensionsFromJSON,
    DimensionsFromJSONTyped,
    DimensionsToJSON,
    DimensionsToJSONTyped,
} from './Dimensions';
import type { Link } from './Link';
import {
    LinkFromJSON,
    LinkFromJSONTyped,
    LinkToJSON,
    LinkToJSONTyped,
} from './Link';

/**
 * A batchOfGoods is a group of physical objects that are managed and tracked together as a single unit, even though they may not be individually identified. For example, a batchOfGoods may contain a number of pallets which cannot be individually identified as pieces. Depending on the industry maturity and constraints, the level of description of the logistics units is variable. By using batchOfGoods or pieces the stakeholders can describe the ad'hock level of management of the logistics units. This is often done for efficiency in the logistics chain, as it allows multiple items to be handled and tracked as a single entity.
 * @export
 * @interface BatchOfGoods
 */
export interface BatchOfGoods {
    /**
     * Packaging code of the non identified items.
     * @type {string}
     * @memberof BatchOfGoods
     */
    codePackaging?: string;
    /**
     * Subtype to specify the type of container, such as the standard size of the pallet. The list of values is the same as container types defined for pieces.
     * @type {string}
     * @memberof BatchOfGoods
     */
    containerSubtype?: string;
    /**
     * Type of logistic units containing the goods. For example 'PALLET', 'PARCEL', .... List of value is the same as container types of pieces.
     * @type {string}
     * @memberof BatchOfGoods
     */
    containerType?: string;
    /**
     * Size of each object composing the batch of goods.
     * @type {Dimensions}
     * @memberof BatchOfGoods
     */
    dimensions?: Dimensions;
    /**
     * 
     * @type {Link}
     * @memberof BatchOfGoods
     */
    product?: Link;
    /**
     * Special regulations applied to the batch of goods.
     * @type {Array<SpecialRegulation>}
     * @memberof BatchOfGoods
     */
    specialRegulations?: Array<SpecialRegulation>;
    /**
     * Quantity of objects in the batch of goods.
     * @type {Value}
     * @memberof BatchOfGoods
     */
    quantity?: Value;
    /**
     * Weight of each object composing the batch of goods.
     * @type {Value}
     * @memberof BatchOfGoods
     */
    weight?: Value;
    /**
     * Amount declared for insurance or customs declaration purpose.
     * @type {Value}
     * @memberof BatchOfGoods
     */
    declaredValue?: Value;
}

/**
 * Check if a given object implements the BatchOfGoods interface.
 */
export function instanceOfBatchOfGoods(value: object): value is BatchOfGoods {
    return true;
}

export function BatchOfGoodsFromJSON(json: any): BatchOfGoods {
    return BatchOfGoodsFromJSONTyped(json, false);
}

export function BatchOfGoodsFromJSONTyped(json: any, ignoreDiscriminator: boolean): BatchOfGoods {
    if (json == null) {
        return json;
    }
    return {
        
        'codePackaging': json['codePackaging'] == null ? undefined : json['codePackaging'],
        'containerSubtype': json['containerSubtype'] == null ? undefined : json['containerSubtype'],
        'containerType': json['containerType'] == null ? undefined : json['containerType'],
        'dimensions': json['dimensions'] == null ? undefined : DimensionsFromJSON(json['dimensions']),
        'product': json['product'] == null ? undefined : LinkFromJSON(json['product']),
        'specialRegulations': json['specialRegulations'] == null ? undefined : ((json['specialRegulations'] as Array<any>).map(SpecialRegulationFromJSON)),
        'quantity': json['quantity'] == null ? undefined : ValueFromJSON(json['quantity']),
        'weight': json['weight'] == null ? undefined : ValueFromJSON(json['weight']),
        'declaredValue': json['declaredValue'] == null ? undefined : ValueFromJSON(json['declaredValue']),
    };
}

export function BatchOfGoodsToJSON(json: any): BatchOfGoods {
    return BatchOfGoodsToJSONTyped(json, false);
}

export function BatchOfGoodsToJSONTyped(value?: BatchOfGoods | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'codePackaging': value['codePackaging'],
        'containerSubtype': value['containerSubtype'],
        'containerType': value['containerType'],
        'dimensions': DimensionsToJSON(value['dimensions']),
        'product': LinkToJSON(value['product']),
        'specialRegulations': value['specialRegulations'] == null ? undefined : ((value['specialRegulations'] as Array<any>).map(SpecialRegulationToJSON)),
        'quantity': ValueToJSON(value['quantity']),
        'weight': ValueToJSON(value['weight']),
        'declaredValue': ValueToJSON(value['declaredValue']),
    };
}

